import Accordion from 'LuxKit/Accordion'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import React from 'react'

export default function BookingManagement() {
  return (
    <Group direction="vertical" gap={8}>
      <Heading variant="heading4">Booking management</Heading>
      <Accordion size="large" heading="How do I change/cancel a booking for a traveller in my company after it has been made?">
        <BodyText variant="large">
          If you need to change or cancel a booking for a traveller in your company, please follow these steps:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Contact Customer Service:</BodyText>
              <BodyText variant="large"> To make any changes to your booking, including modifications or cancellations, please reach out to our Customer Service team directly.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Provide Booking Details:</BodyText>
              <BodyText variant="large"> When contacting Customer Service, be sure to provide the relevant booking details, including the traveller's name, booking reference number, and your specific changes or requests.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Discuss Options:</BodyText>
              <BodyText variant="large"> Our Customer Service team will assist you in exploring available options for modifying or cancelling the booking. They will provide guidance on any applicable fees, restrictions, or conditions associated with the changes.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Confirm Changes:</BodyText>
              <BodyText variant="large"> Once you have discussed the options with our Customer Service team, they will process the requested changes or cancellations on your behalf. Be sure to review and confirm the changes before finalising the transaction.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Receive Confirmation:</BodyText>
              <BodyText variant="large"> After the changes have been processed, you will receive confirmation of the updated booking details or cancellation. Please retain this confirmation for your records.</BodyText>
            </li>
          </Group>
        </ol>
        <BodyText variant="large">
          Please note that if the booking is within its non-refundable period, you may not be able to receive a refund and the accommodation may be forfeited.
        </BodyText>
        <br />
        <BodyText variant="large">
          If you have any further enquiries or require assistance, please don't hesitate to contact our Customer Service team.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="What are the payment options available to staff? ">
        <BodyText variant="large">
          Currently, team members have the option to use their personal credit cards for making payments related to corporate travel bookings. When using personal credit cards, employees will be responsible for managing their own payment transactions and may need to seek reimbursement from the company according to established reimbursement procedures.
        </BodyText>
        <br />
        <BodyText variant="large">
          Please be advised that a central payment system is planned for implementation in the future, which will offer additional convenience and streamlined payment processes for staff members. Once the central payment system is in place, staff members will have access to centralised billing options, simplifying the payment and expense management process for corporate travel.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="Can I as an admin book travel on behalf of other travellers?">
        <BodyText variant="large">
          Yes, you have the ability to book travel on behalf of other travellers within your business. Here's how:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Access the Homepage:</BodyText>
              <BodyText variant="large"> Navigate to the homepage of the booking platform.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Select Traveller Dropdown:</BodyText>
              <BodyText variant="large"> On the homepage, locate the traveller dropdown menu, which allows you to choose the traveller for whom you would like to make the booking.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Proceed with Booking:</BodyText>
              <BodyText variant="large"> Once you have selected the traveller, proceed with making the booking as normal. You can search for flights and / or hotels based on the selected traveller's requirements.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Complete the Booking:</BodyText>
              <BodyText variant="large"> After selecting the desired travel options, proceed to confirm and finalise the booking. Ensure that all details are accurate and review any applicable terms and conditions before completing the reservation.</BodyText>
            </li>
          </Group>
        </ol>
        <BodyText variant="large">
          Administrators can easily book travel on behalf of other travellers within their business, streamlining the booking process and ensuring efficient management of corporate travel arrangements.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="Can I book multiple rooms at one time?">
        <BodyText variant="large">
          Currently, our booking system supports booking for one traveller at a time, and it does not offer the capability to book multiple rooms simultaneously. If you need to arrange accommodations for multiple travellers or require multiple rooms for a single traveller, each booking will need to be made individually.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="What can I do with an existing flight for an employee who has left the business?">
        <BodyText variant="large">
          Depending on the terms and conditions of the airfare booked, the fare may be refundable or refundable less a penalty. Even for non-refundable tickets, you are still able to get any unused taxes back.  All refund requests must be made at least 24 hours prior to departure. Refunds can take approximately 12 weeks to be processed by the airline and will be processed back to the same method of payment. To check any fare terms and conditions, please contact our support team.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="What can I do with an existing accommodation booking for an employee who has left the business?">
        <BodyText variant="large">
          Managing an existing accommodation booking for an employee who has left the business can vary depending on the hotel's policy and the timing of the request. Here's how you can proceed:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Review Hotel Policy:</BodyText>
              <BodyText variant="large"> Begin by reviewing the cancellation and refund policy of the particular hotel where the accommodation was booked. The hotel's policy will dictate the options available for modifying or cancelling the reservation.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Check Refund Period:</BodyText>
              <BodyText variant="large"> Determine if the booking falls within the hotel's refund period. If the booking is still within this period, you may be entitled to a full refund or a refund to credit, depending on the hotel's terms and conditions.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Request Refund:</BodyText>
              <BodyText variant="large"> If the booking is eligible for a refund according to the hotel's policy, contact the hotel or the booking platform, through which the reservation was made, to request a refund. Provide relevant details, such as the booking reference number and the reason for cancellation.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Non-Refundable Accommodation:</BodyText>
              <BodyText variant="large"> If the booking is outside the hotel's refund period or if it was made as a non-refundable reservation, the accommodation may be non-refundable. In this case, you may not be able to obtain a refund, and the cost of the booking may be forfeited.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Consider Alternatives:</BodyText>
              <BodyText variant="large"> If a refund is not possible, consider alternative options such as transferring the booking to another employee within the company, if applicable, or exploring the possibility of modifying the reservation dates, if permitted by the hotel.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Document Communication:</BodyText>
              <BodyText variant="large"> Keep records of all communication with the hotel or booking platform regarding the accommodation booking, including any refund requests, responses received and actions taken.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="Can I book for a guest traveller?">
        <BodyText variant="large">
          Currently, it is not possible to book for a guest traveller through our platform. Our booking system is designed to facilitate travel arrangements exclusively for travellers who have been invited to your company. This ensures that bookings are made within the established framework of your corporate travel management system and adhere to company policies and procedures.
        </BodyText>
        <br />
        <BodyText variant="large">
          If you have any questions or require further clarification, please feel free to contact our Customer Service team for assistance.
        </BodyText>
      </Accordion>
    </Group>
  )
}
